import type { MfaEnrollFactorType } from '@auth0/auth0-acul-react/types';
import {
  enroll,
  useMfaBeginEnrollOptions,
  useScreen,
  useTenant,
} from '@auth0/auth0-acul-react/mfa-begin-enroll-options';

import { executeSafely } from '@/utils/helpers/executeSafely';

// Only handle SMS enrollment on this screen
const SUPPORTED_FACTORS: MfaEnrollFactorType[] = ['sms'];

export const useMfaBeginEnrollOptionsManager = () => {
  const mfaBeginEnrollOptions = useMfaBeginEnrollOptions();
  const { texts } = useScreen();
  const { enabledFactors } = useTenant();

  // Factor availability comes from the tenant configuration, filtered to
  // only the factors this screen knows how to render.
  const supportedEnrolledFactors = (enabledFactors ?? []).filter((factor) =>
    SUPPORTED_FACTORS.includes(factor as MfaEnrollFactorType),
  );

  const handleEnrollSms = async (): Promise<void> => {
    await executeSafely('Enroll into the MFA factor: sms', () =>
      enroll({ action: 'sms' as MfaEnrollFactorType }),
    );
  };

  // Skips SMS enrollment for now. The Auth0 tenant's MFA policy/Action
  // handles this "ignore" action server-side by continuing the login flow
  // with an alternative already-available factor (e.g. email challenge),
  // instead of forcing SMS enrollment.
  const handleIgnore = async (): Promise<void> => {
    await executeSafely('Ignore MFA enrollment', () =>
      enroll({ action: 'ignore' as MfaEnrollFactorType }),
    );
  };

  return {
    mfaBeginEnrollOptions,
    handleEnrollSms,
    handleIgnore,
    texts: texts ?? {},
    supportedEnrolledFactors,
  };
};
